// copy-register: commercial
import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { groundworksEntityPath } from "../catalogue";
import { useCopy, useFramework } from "../framework";
import {
  FactRow,
  Figure,
  PageHeader,
  SectionHeading,
  Trail,
  humanize,
  ordinal,
} from "../components/ui";

const sections = [
  { id: "structure", labelKey: "page.data-model.section.structure" },
  { id: "semantic-rules", labelKey: "page.data-model.section.semantic-rules" },
  { id: "dimensions", labelKey: "page.data-model.section.dimensions" },
  { id: "ai-readiness", labelKey: "page.data-model.section.ai-readiness" },
  { id: "workblock-compatibility", labelKey: "page.data-model.section.workblock-compatibility" },
  { id: "proven-boundary", labelKey: "page.data-model.section.proven-boundary" },
  { id: "explore", labelKey: "page.data-model.section.explore" },
] as const;

const structureLayers = ["semantic", "record", "graph", "exchange"] as const;

const semanticRules = [
  { id: "entity", example: "spend-classification-decision" },
  { id: "primary-key", example: "spend-classification-decision-id" },
  { id: "reference", example: "category-id · evidence-item-ids" },
  { id: "polymorphic", example: "decided-by-ref → person | role" },
  { id: "lifecycle", example: "lifecycle-state" },
  {
    id: "vocabulary",
    example: "resolution-source = transaction-line-override | item-or-catalogue-default | category-assignment | proposal-only | unresolved",
  },
] as const;

const precedenceSteps = [
  "committed-line",
  "item-default",
  "category-assignment",
  "proposal",
  "unresolved",
] as const;

const aiMechanisms = [
  "semantics",
  "constraints",
  "traversal",
  "truth",
  "context",
  "unresolved",
] as const;

const workblockLayers = ["definition", "owner", "projection"] as const;

const presentBoundaryItems = [
  "schema",
  "ownership",
  "dimensions",
  "projections",
] as const;

const withheldBoundaryItems = [
  "cue",
  "database",
  "permission",
  "lake",
  "tenant",
  "production",
] as const;

function useFocusedHashTarget(): void {
  const { hash } = useLocation();

  useEffect(() => {
    const focusTarget = (nextHash: string) => {
      if (!nextHash) return;
      let id: string;
      try {
        id = decodeURIComponent(nextHash.slice(1));
      } catch {
        return;
      }
      const target = document.getElementById(id);
      if (!target) return;
      if (typeof target.scrollIntoView === "function") target.scrollIntoView();
      target.focus({ preventScroll: true });
    };

    focusTarget(hash);
    const handleHashChange = () => focusTarget(window.location.hash);
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [hash]);
}

function AnchoredSection({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section
      className="apf-page-section apf-data-model-landing__section"
      id={id}
      tabIndex={-1}
      aria-labelledby={`${id}-title`}
    >
      <SectionHeading id={`${id}-title`} title={title} description={description} />
      {children}
    </section>
  );
}

/** The public explanation of the semantic contract behind the GroundWorks catalogue. */
export function DataModelPage() {
  const copy = useCopy();
  const { snapshot } = useFramework();
  useFocusedHashTarget();

  const entities = snapshot.groundworks.entities;
  const entityById = new Map(entities.map((entity) => [entity.id, entity]));
  const fieldCount = entities.reduce((total, entity) => total + entity.fields.length, 0);
  const dimensions = snapshot.groundworks.dimensions;
  const relationshipSource = entityById.get("spend-classification-decision");
  const relationshipTargetOrder = [
    "spend-record",
    "authority-envelope",
    "evidence-item",
    "spend-classification-decision",
  ];
  const relationshipExamples = relationshipSource
    ? relationshipTargetOrder.flatMap((targetEntityId) => {
        const relationship = relationshipSource.relationships.find(
          (candidate) => candidate.targetEntityId === targetEntityId,
        );
        const target = entityById.get(targetEntityId);
        return relationship && target ? [{ relationship, target }] : [];
      })
    : [];

  return (
    <div className="apf-page apf-register-reference apf-data-model-landing">
      <PageHeader
        eyebrow={copy("page.data-model.eyebrow")}
        title={copy("page.data-model.title")}
        description={copy("page.data-model.description")}
      />

      <nav
        className="apf-data-model-landing__contents"
        aria-label={copy("page.data-model.contents-label")}
      >
        <ol>
          {sections.map((section, index) => (
            <li key={section.id}>
              <a href={`#${section.id}`}>
                <span aria-hidden="true">{ordinal(index + 1)}</span>
                {copy(section.labelKey)}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <AnchoredSection
        id="structure"
        title={copy("page.data-model.structure.title")}
        description={copy("page.data-model.structure.description")}
      >
        <FactRow
          facts={[
            {
              label: copy("page.data-model.structure.fact.records"),
              value: entities.length,
              to: "/groundworks?section=entities#data-model-catalogue-content",
            },
            {
              label: copy("page.data-model.structure.fact.fields"),
              value: fieldCount,
              to: "/groundworks?section=fields#data-model-catalogue-content",
            },
            {
              label: copy("page.data-model.structure.fact.dimensions"),
              value: dimensions.length,
              to: "/groundworks?section=dimensions#data-model-catalogue-content",
            },
          ]}
        />
        <Figure lead={copy("page.data-model.structure.figure-lead")}>
          <ol
            className="apf-data-model-structure"
            aria-label={copy("page.data-model.structure.figure-label")}
          >
            {structureLayers.map((layer, index) => (
              <li key={layer}>
                <span aria-hidden="true">{ordinal(index + 1)}</span>
                <div>
                  <h3>{copy(`page.data-model.structure.${layer}.title`)}</h3>
                  <p>{copy(`page.data-model.structure.${layer}.body`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </Figure>
      </AnchoredSection>

      <AnchoredSection
        id="semantic-rules"
        title={copy("page.data-model.semantic.title")}
        description={copy("page.data-model.semantic.description")}
      >
        <ol className="apf-data-model-semantic-rules">
          {semanticRules.map((rule, index) => (
            <li key={rule.id}>
              <span aria-hidden="true">{ordinal(index + 1)}</span>
              <div>
                <h3>{copy(`page.data-model.semantic.rule.${rule.id}.title`)}</h3>
                <p>{copy(`page.data-model.semantic.rule.${rule.id}.body`)}</p>
                <code>{rule.example}</code>
              </div>
            </li>
          ))}
        </ol>

        {relationshipSource && relationshipExamples.length ? (
          <div className="apf-data-model-relationships">
            <h3>{copy("page.data-model.semantic.relationship.title")}</h3>
            <p>{copy("page.data-model.semantic.relationship.body")}</p>
            <ul aria-label={copy("page.data-model.semantic.relationship.example-label")}>
              {relationshipExamples.map(({ relationship, target }) => (
                <li key={relationship.qualifiedId}>
                  <div className="apf-data-model-relationships__path">
                    <Link to={groundworksEntityPath(relationshipSource.id)}>{relationshipSource.label}</Link>
                    <span>{humanize(relationship.predicate).toLowerCase()}</span>
                    <Link to={groundworksEntityPath(target.id)}>{target.label}</Link>
                  </div>
                  <dl>
                    <div>
                      <dt>{copy("page.data-model.semantic.relationship.join-label")}</dt>
                      <dd>
                        <code>{relationship.sourceFieldId}</code>
                        <span aria-hidden="true">→</span>
                        <code>{relationship.targetFieldId}</code>
                      </dd>
                    </div>
                    <div>
                      <dt>{copy("page.data-model.semantic.relationship.cardinality-label")}</dt>
                      <dd>{humanize(relationship.cardinality)}</dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </AnchoredSection>

      <AnchoredSection
        id="dimensions"
        title={copy("page.data-model.dimensions.title")}
        description={copy("page.data-model.dimensions.description")}
      >
        <p className="apf-data-model-dimensions__independent">
          {copy("page.data-model.dimensions.independent")}
        </p>
        <ul className="apf-data-model-dimensions">
          {dimensions.map((dimension) => {
            const members = [...dimension.members]
              .sort((left, right) => left.order - right.order)
              .map((member) => copy(member.labelKey));
            return (
              <li key={dimension.id}>
                <h3>{copy(dimension.labelKey)}</h3>
                <p>{copy(dimension.definitionKey)}</p>
                <div>
                  <span>{copy("page.data-model.dimensions.members-label")}</span>
                  <strong>
                    {members.length
                      ? members.join(" · ")
                      : copy("page.data-model.dimensions.tenant-values")}
                  </strong>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="apf-data-model-precedence">
          <h3>{copy("page.data-model.dimensions.precedence.title")}</h3>
          <p>{copy("page.data-model.dimensions.precedence.description")}</p>
          <ol>
            {precedenceSteps.map((step, index) => (
              <li key={step}>
                <span aria-hidden="true">{ordinal(index + 1)}</span>
                <p>{copy(`page.data-model.dimensions.precedence.${step}`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </AnchoredSection>

      <AnchoredSection
        id="ai-readiness"
        title={copy("page.data-model.ai.title")}
        description={copy("page.data-model.ai.description")}
      >
        <div className="apf-frame apf-frame--inverse apf-data-model-ai__statement">
          <h3>{copy("page.data-model.ai.statement.title")}</h3>
          <p>{copy("page.data-model.ai.statement.body")}</p>
        </div>
        <ul className="apf-data-model-ai__mechanisms">
          {aiMechanisms.map((mechanism) => (
            <li key={mechanism}>
              <h3>{copy(`page.data-model.ai.mechanism.${mechanism}.title`)}</h3>
              <p>{copy(`page.data-model.ai.mechanism.${mechanism}.body`)}</p>
            </li>
          ))}
        </ul>
        <p className="apf-data-model-ai__limit">{copy("page.data-model.ai.limit")}</p>
      </AnchoredSection>

      <AnchoredSection
        id="workblock-compatibility"
        title={copy("page.data-model.workblock.title")}
        description={copy("page.data-model.workblock.description")}
      >
        <ol className="apf-data-model-workblock-flow">
          {workblockLayers.map((layer, index) => (
            <li key={layer}>
              <span aria-hidden="true">{ordinal(index + 1)}</span>
              <h3>{copy(`page.data-model.workblock.${layer}.title`)}</h3>
              <p>{copy(`page.data-model.workblock.${layer}.body`)}</p>
            </li>
          ))}
        </ol>
        <p className="apf-data-model-workblock-note">{copy("page.data-model.workblock.note")}</p>
      </AnchoredSection>

      <AnchoredSection
        id="proven-boundary"
        title={copy("page.data-model.boundary.title")}
        description={copy("page.data-model.boundary.description")}
      >
        <div className="apf-data-model-boundary">
          <section aria-labelledby="data-model-boundary-present-title">
            <h3 id="data-model-boundary-present-title">
              {copy("page.data-model.boundary.present.title")}
            </h3>
            <ul>
              {presentBoundaryItems.map((item) => (
                <li key={item}>{copy(`page.data-model.boundary.present.${item}`)}</li>
              ))}
            </ul>
          </section>
          <section aria-labelledby="data-model-boundary-withheld-title">
            <h3 id="data-model-boundary-withheld-title">
              {copy("page.data-model.boundary.withheld.title")}
            </h3>
            <ul>
              {withheldBoundaryItems.map((item) => (
                <li key={item}>{copy(`page.data-model.boundary.withheld.${item}`)}</li>
              ))}
            </ul>
          </section>
        </div>
      </AnchoredSection>

      <AnchoredSection
        id="explore"
        title={copy("page.data-model.explore.title")}
        description={copy("page.data-model.explore.description")}
      >
        <Trail
          label={copy("page.data-model.explore.trail-label")}
          items={[
            {
              to: "/groundworks?section=entities#data-model-catalogue-content",
              glyph: "class.record",
              label: copy("page.data-model.explore.catalogue.label"),
              context: copy("page.data-model.explore.catalogue.context"),
            },
            {
              to: "/groundworks/category",
              glyph: "class.record",
              label: copy("page.data-model.explore.category.label"),
              context: copy("page.data-model.explore.category.context"),
            },
            {
              to: "/groundworks/spend-classification-decision",
              glyph: "class.record",
              label: copy("page.data-model.explore.decision.label"),
              context: copy("page.data-model.explore.decision.context"),
            },
            {
              to: "/groundworks?section=standards-registry#data-model-catalogue-content",
              glyph: "class.record",
              label: copy("page.data-model.explore.standards.label"),
              context: copy("page.data-model.explore.standards.context"),
            },
          ]}
        />
      </AnchoredSection>
    </div>
  );
}
